import { pdf } from "@react-pdf/renderer";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Tooltip } from "antd";
import { StyledButton } from "common/components/StyledElements";
import { RMPM_ROLE } from "common/constants";
import APIConstants from "common/constants/APIConstants";
import { useAuthContext } from "common/contexts/AuthContext";
import backArrow from "common/icons/backArrow.svg";
import DownloadIcon from "common/icons/downloadwhite.png";
import { formatNames, getDateTime, getUserRole } from "common/utils";
import { api } from "common/utils/APIMethods";
import { showMessage } from "common/utils/message";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import DownloadPdf from "./downloadPdf";

const Wrapper = styled.div`
  padding: 24px;
  background: linear-gradient(to right, #f8f9fb, #eef1f6);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  margin: 0;
  font-weight: 600;
  color: #222;
`;

const Card = styled.div`
  background: #ffffff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const StatusBadge = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ bg }) => "black"};
  padding: 6px 9px;
  border-radius: 6px;
  background-color: ${({ bg }) => bg || "#ece4aa"};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FullWidth = styled(Field)`
  grid-column: span 2;

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #777;
  text-transform: uppercase;
  min-width: 150px;
`;

const Value = styled.span`
  font-size: 15px;
  color: #222;
  word-break: break-word;
`;

const AddStyledButton = styled(StyledButton)`
  padding: 8px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AddImg = styled.img`
  width: 18px;
  height: 18px;
`;

const Empty = styled.h3`
  padding: 40px;
  text-align: center;
`;

const TitleHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 10px;
  color: #005754;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
  padding-bottom: 10px;
`;

const BackImage = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

const PODetails = () => {
  const { authConfig } = useAuthContext();
  const role = getUserRole(authConfig);
  const isRoleScm = role === RMPM_ROLE.scm;
  const isRoleMasterAdmin = role === RMPM_ROLE.masterAdmin;
  const navigate = useNavigate();
  const order = useLocation().state;

  const { data: allStatuses = [] } = useQuery({
    queryKey: ["allStatuses"],
    queryFn: () =>
      api(
        {
          url: APIConstants.getPurchaseStatuses
        },
        authConfig
      )
  });

  const {
    data: poData = {},
    isLoading: isPoDataLoading,
    refetch
  } = useQuery({
    queryKey: ["poData"],
    queryFn: () =>
      api(
        {
          url: `${APIConstants.getScmPo}/${order?.id}`
        },
        authConfig
      )
  });

  const { data: companyDetails = [] } = useQuery({
    queryKey: ["companyDetails"],
    queryFn: () =>
      api(
        {
          url: APIConstants.getCompanyDetails
        },
        authConfig
      )
  });

  const {
    mutate: StatusUpdateMutation,
    isPending: StatusUpdateMutationIsPending
  } = useMutation({
    mutationFn: ({ id, statusId }) =>
      api(
        {
          url: `${APIConstants.updatePoStatuses}/${id}`,
          method: "PUT",
          params: { statusId }
        },
        authConfig
      ),
    onSuccess: () => {
      showMessage("Updates Status Successfully");
      refetch();
    },
    onError: () => {
      showMessage("status update failed");
    }
  });

  const handleUpdateStatus = (key, record) => {
    StatusUpdateMutation({ id: record?.id, statusId: key });
  };

  const currentStatus = poData?.scmPoStatus?.id;

  const nextStatusItem = allStatuses.find((s) => s?.id === currentStatus + 1);

  const key = nextStatusItem?.id;
  const dataLabel = nextStatusItem?.status.replaceAll("_", "");

  let color = "black";
  if (currentStatus === 1) {
    color = "#4ecdc4";
  } else if (currentStatus === 2) {
    color = "#c9b3e2ff";
  } else if (currentStatus === 3) {
    color = "#ffd97d";
  }

  const companyId = poData?.companyHead?.id;

  const { data: generalConditions = [] } = useQuery({
    queryKey: ["generalConditions"],
    queryFn: () =>
      api(
        {
          url: `${APIConstants.getGeneralConditions}/${companyId}`
        },
        authConfig
      )
  });

  const { data: address = [] } = useQuery({
    queryKey: ["address"],
    queryFn: () =>
      api(
        {
          url: APIConstants.getUserAddress
        },
        authConfig
      )
  });

  const handleDownload = async (record) => {
    try {
      const blob = await pdf(<DownloadPdf companyDetails={record} />).toBlob();

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `PO_${formatNames(record?.materialName)}_${getDateTime()}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleGoBack = () => {
    navigate("/scm-po");
  };

  if (!poData) {
    return <Empty>No Data Available</Empty>;
  }

  if (isPoDataLoading) {
    return <Empty>Loading...</Empty>;
  }
  return (
    <Wrapper>
      <Header>
        <TitleHeaderContainer>
          <BackImage src={backArrow} onClick={handleGoBack} alt="back" />
          <div>Purchase Order Details</div>
        </TitleHeaderContainer>

        <ButtonGroup>
          <StatusBadge bg={color}>
            Status: {poData?.scmPoStatus?.status.replaceAll("_", " ")}
          </StatusBadge>
          {isRoleScm && dataLabel && (
            <AddStyledButton
              type="primary"
              disabled={StatusUpdateMutationIsPending}
              onClick={() => handleUpdateStatus(key, poData)}
            >
              Update to {dataLabel}
            </AddStyledButton>
          )}

          {!isRoleMasterAdmin && (
            <Tooltip
              title="Download PDF"
              trigger={["hover"]}
              color="rgba(0, 0, 0, 0.70)"
              placement="topLeft"
            >
              <AddStyledButton
                type="primary"
                onClick={() =>
                  handleDownload({
                    ...poData,
                    generalConditions,
                    companyAddress: address,
                    companyDetails
                  })
                }
              >
                <AddImg src={DownloadIcon} />
              </AddStyledButton>
            </Tooltip>
          )}
        </ButtonGroup>
      </Header>

      <Card>
        <Grid>
          <Field>
            <Label>PO Number:</Label>
            <Value>{poData?.poNumber}</Value>
          </Field>

          <Field>
            <Label>Vendor Name:</Label>
            <Value>{poData?.vendorName}</Value>
          </Field>

          <Field>
            <Label>Material Code:</Label>
            <Value>{poData?.materialCode}</Value>
          </Field>

          <Field>
            <Label>Material Name:</Label>
            <Value>{poData?.materialName}</Value>
          </Field>

          <Field>
            <Label>Mobile Number:</Label>
            <Value>{poData?.mobileNumber}</Value>
          </Field>

          <Field>
            <Label>Email:</Label>
            <Value>{poData?.email}</Value>
          </Field>

          <Field>
            <Label>Reference Number:</Label>
            <Value>{poData?.referenceNumber}</Value>
          </Field>

          <Field>
            <Label>Insurance:</Label>
            <Value>{poData?.insurance}</Value>
          </Field>

          <Field>
            <Label>Freight:</Label>
            <Value>{poData?.freight}</Value>
          </Field>

          <Field>
            <Label>CAS Number:</Label>
            <Value>{poData?.casNumber}</Value>
          </Field>

          <Field>
            <Label>Shipment:</Label>
            <Value>{poData?.shipment}</Value>
          </Field>

          <Field>
            <Label>Currency:</Label>
            <Value>{poData?.currency}</Value>
          </Field>

          <Field>
            <Label>Payment:</Label>
            <Value>{poData?.payment}</Value>
          </Field>

          <Field>
            <Label>Discount:</Label>
            <Value>{poData?.discount}%</Value>
          </Field>

          <Field>
            <Label>Quantity:</Label>
            <Value>{poData?.quantity}</Value>
          </Field>

          <Field>
            <Label>Rate:</Label>
            <Value>{poData?.rate}</Value>
          </Field>

          <Field>
            <Label>Subtotal:</Label>
            <Value>{poData?.subTotal}</Value>
          </Field>

          <Field>
            <Label>Total:</Label>
            <Value>{poData?.total}</Value>
          </Field>

          <Field>
            <Label>Received Quantity:</Label>
            <Value>{poData?.receivedQuantity ?? "Not Received"}</Value>
          </Field>

          <Field>
            <Label>Address:</Label>
            <Value>{poData?.address}</Value>
          </Field>

          <FullWidth>
            <Label>Note:</Label>
            <Value>{poData?.note}</Value>
          </FullWidth>
        </Grid>
      </Card>
    </Wrapper>
  );
};

export default PODetails;
